/**
 * Chat Service
 * Handles all chat-related API calls. Follows CHAT_DEMO_SPEC.md.
 * httpClient adds: Authorization, X-Tenant-ID, X-Device-ID, Content-Type.
 * @module services/chat/chatService
 */

import httpClient from '@/utils/httpClient';
import { ENDPOINTS, getEndpointPath } from '@/api/endpoints';
import { createIdempotencyKey, idempotencyHeaders } from '@/utils/idempotency';

function withClientMessageId(messageData = {}) {
  const clientMessageId =
    (typeof messageData.clientMessageId === 'string' && messageData.clientMessageId.trim()) ||
    createIdempotencyKey();
  return {
    body: { ...messageData, clientMessageId },
    clientMessageId,
  };
}

const chatService = {
  getEmployees: async () => {
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.CHAT.EMPLOYEES));
      return response.data;
    } catch (error) {
      console.error('Error fetching employees for chat:', error);
      return {
        success: true,
        data: []
      };
    }
  },

  getMessages: async (employeeId) => {
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.CHAT.MESSAGES, employeeId));
      return response.data;
    } catch (error) {
      console.error('Error fetching messages:', error);
      return {
        success: true,
        data: []
      };
    }
  },

  sendMessage: async (messageData) => {
    try {
      const { body, clientMessageId } = withClientMessageId(messageData);
      const response = await httpClient.post(
        getEndpointPath(ENDPOINTS.CHAT.SEND_MESSAGE),
        body,
        { headers: idempotencyHeaders({}, clientMessageId) }
      );
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error.response?.data || error;
    }
  },

  markAsRead: async (employeeId) => {
    try {
      const response = await httpClient.patch(getEndpointPath(ENDPOINTS.CHAT.MARK_AS_READ, employeeId));
      return response.data;
    } catch (error) {
      console.error('Error marking messages as read:', error);
      throw error.response?.data || error;
    }
  },

  // Group Chat Methods
  getGroups: async () => {
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.CHAT.GROUPS));
      return response.data;
    } catch (error) {
      console.error('Error fetching chat groups:', error);
      throw error.response?.data || error;
    }
  },

  getGeneralMessages: async (page = 1, limit = 50) => {
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.CHAT.MESSAGES_GENERAL(page, limit)));
      return response.data;
    } catch (error) {
      console.error('Error fetching general messages:', error);
      throw error.response?.data || error;
    }
  },

  getDepartmentMessages: async (departmentId, page = 1, limit = 50) => {
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.CHAT.MESSAGES_DEPARTMENT(departmentId, page, limit)));
      return response.data;
    } catch (error) {
      console.error('Error fetching department messages:', error);
      throw error.response?.data || error;
    }
  },

  sendGroupMessage: async (messageData) => {
    try {
      const { body, clientMessageId } = withClientMessageId(messageData);
      const response = await httpClient.post(
        getEndpointPath(ENDPOINTS.CHAT.SEND_MESSAGE),
        body,
        { headers: idempotencyHeaders({}, clientMessageId) }
      );
      return response.data;
    } catch (error) {
      console.error('Error sending group message:', error);
      throw error.response?.data || error;
    }
  },

  deleteMessage: async (messageId) => {
    try {
      const response = await httpClient.delete(getEndpointPath(ENDPOINTS.CHAT.DELETE_MESSAGE, messageId));
      return response.data;
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error.response?.data || error;
    }
  },

  searchMessages: async (groupType, query, departmentId = null, page = 1, limit = 20) => {
    try {
      const response = await httpClient.get(
        getEndpointPath(ENDPOINTS.CHAT.SEARCH_MESSAGES(groupType, query, departmentId, page, limit))
      );
      return response.data;
    } catch (error) {
      console.error('Error searching messages:', error);
      throw error.response?.data || error;
    }
  },

  getMessagesAround: async (messageId, before = 25, after = 25) => {
    try {
      const response = await httpClient.get(
        getEndpointPath(ENDPOINTS.CHAT.MESSAGES_AROUND(messageId, before, after))
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching messages around:', error);
      throw error.response?.data || error;
    }
  },

  getPinnedMessages: async (groupType, departmentId = null) => {
    try {
      const response = await httpClient.get(
        getEndpointPath(ENDPOINTS.CHAT.PINNED_MESSAGES(groupType, departmentId))
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching pinned messages:', error);
      return { success: true, data: [] };
    }
  },

  toggleReaction: async (messageId, emoji) => {
    try {
      const response = await httpClient.post(
        getEndpointPath(ENDPOINTS.CHAT.TOGGLE_REACTION, messageId),
        { emoji }
      );
      return response.data;
    } catch (error) {
      console.error('Error toggling reaction:', error);
      throw error.response?.data || error;
    }
  },

  pinMessage: async (messageId) => {
    try {
      const response = await httpClient.post(getEndpointPath(ENDPOINTS.CHAT.PIN_MESSAGE, messageId));
      return response.data;
    } catch (error) {
      console.error('Error pinning message:', error);
      throw error.response?.data || error;
    }
  },

  unpinMessage: async (messageId) => {
    try {
      const response = await httpClient.delete(getEndpointPath(ENDPOINTS.CHAT.UNPIN_MESSAGE, messageId));
      return response.data;
    } catch (error) {
      console.error('Error unpinning message:', error);
      throw error.response?.data || error;
    }
  }
};

export default chatService;

